/* Stratus content pages: anchor navigation (assets/st-content-a-anchors.js). No library.
 *
 * Contract: nav[data-st-anchor-nav] holding a[href^="#"] links (Page Head tabs, FAQ topics). Each link points at an
 * element with an id (the section or group it names, marked data-st-anchor-target).
 *   - The link of the section being read is marked: aria-current="true" and .is-active. A section is "being read"
 *     once its top has passed a line a little under the sticky header, and it stays so until the next one passes;
 *     at the very end of the page the last section that is on screen wins. Before the first one, the first link
 *     keeps the mark.
 *   - A click scrolls to the target (smooth unless prefers-reduced-motion), puts the hash in the address with
 *     history.replaceState, and moves focus to the target (tabindex="-1") so the next Tab starts there. While the
 *     scroll runs, the mark stays on the clicked link instead of flicking through the sections in between.
 *   - With data-st-anchor-nav="chips" the nav is a row that scrolls sideways under 1024: the active chip is kept in
 *     view by scrolling that row only, never the page (no scrollIntoView on the chip).
 *   - Without script the links still jump (they are plain #hash links).
 * Safe to load more than once on a page (every section that uses it adds the tag) and on section reload in the
 * theme editor: navs are initialised once each, and the scroll listener is shared.
 */
(function () {
  if (window.stAnchorNav) {
    window.stAnchorNav.init();
    return;
  }

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var items = [];
  var ticking = false;
  var lockedTo = null;
  var idleTimer = 0;
  var listening = false;

  function reduced() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function targetOf(link) {
    var id = (link.getAttribute('href') || '').slice(1);
    try { id = decodeURIComponent(id); } catch (e) { /* keep the raw id */ }
    return id ? document.getElementById(id) : null;
  }

  // The line a section's top has to pass to count as the one being read: the sticky header plus the 24 the targets
  // clear it by (see [data-st-anchor-target] in st-content-a.css), plus a little so a clicked target is already past it.
  function readLine() {
    var header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-height')) || 0;
    return header + 24 + 24;
  }

  function keepChipInView(nav, link) {
    if (getComputedStyle(nav).overflowX === 'visible' || nav.scrollWidth <= nav.clientWidth) return;
    var navBox = nav.getBoundingClientRect();
    var linkBox = link.getBoundingClientRect();
    if (linkBox.left >= navBox.left && linkBox.right <= navBox.right) return;
    var left = nav.scrollLeft + (linkBox.left - navBox.left) - (nav.clientWidth - linkBox.width) / 2;
    nav.scrollTo({ left: Math.max(0, left), behavior: reduced() ? 'auto' : 'smooth' });
  }

  function activate(item, link) {
    if (!link || item.current === link) return;
    item.current = link;
    item.links.forEach(function (a) {
      var on = a === link;
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
    if (item.chips) keepChipInView(item.nav, link);
  }

  function update() {
    ticking = false;
    var line = readLine();
    var atEnd = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 2;
    items.forEach(function (item) {
      if (!item.nav.isConnected) return;
      if (lockedTo && item.links.indexOf(lockedTo) > -1) return;
      var chosen = null;
      item.pairs.forEach(function (p) {
        if (p.target.getBoundingClientRect().top <= line) chosen = p;
      });
      if (atEnd) {
        // The page cannot scroll further: the last section that has come into view is the one being read.
        item.pairs.forEach(function (p) {
          if (p.target.getBoundingClientRect().top < window.innerHeight - 100) chosen = p;
        });
      }
      activate(item, (chosen || item.pairs[0]).link);
    });
  }

  function onScroll() {
    if (lockedTo) {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { lockedTo = null; update(); }, 160);
    }
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }

  function build(nav) {
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    var pairs = links
      .map(function (a) { return { link: a, target: targetOf(a) }; })
      .filter(function (p) { return p.target; });
    if (!pairs.length) return;

    // Targets sorted by their place on the page, so "the last one past the line" is the one being read.
    var ordered = pairs.slice().sort(function (a, b) {
      return (a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
    });
    var item = {
      nav: nav,
      links: links,
      pairs: ordered,
      chips: nav.getAttribute('data-st-anchor-nav') === 'chips',
      current: nav.querySelector('a[aria-current="true"]')
    };
    items.push(item);

    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var target = targetOf(a);
        if (!target) return;
        e.preventDefault();
        lockedTo = a;
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () { lockedTo = null; update(); }, 600);
        activate(item, a);
        target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        if (window.history && history.replaceState) history.replaceState(null, '', a.getAttribute('href'));
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  function init() {
    items = items.filter(function (item) { return item.nav.isConnected; });
    document.querySelectorAll('nav[data-st-anchor-nav]').forEach(function (nav) {
      if (nav.hasAttribute('data-st-anchor-ready')) return;
      nav.setAttribute('data-st-anchor-ready', '');
      build(nav);
    });
    if (items.length && !listening) {
      listening = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
    if (items.length) update();
  }

  window.stAnchorNav = { init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: a reloaded section brings a fresh nav.
  document.addEventListener('shopify:section:load', init);
})();
